// D7: Ad Alerts — mounted feed over the shared ads alert engine contract.
import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { AlertTriangle, Bell, CircleAlert, RefreshCcw } from "lucide-react";

const ALERT_TYPES = [
  { id: "cpl_spike", label: "CPL spike", description: "Cost per lead exceeds the shared engine threshold." },
  { id: "roas_drop", label: "ROAS drop", description: "TRUE ROAS drops below the shared engine threshold." },
  { id: "creative_fatigue", label: "Creative fatigue", description: "CTR trend indicates likely creative fatigue." },
] as const;

interface AdAlert {
  id: string;
  tenantId: string;
  type: string;
  severity: "info" | "warning" | "critical";
  message: string;
  aiRecommendation: string;
  campaignId: string | null;
  campaignName: string | null;
  platform: string | null;
  metricValue: number | null;
  thresholdValue: number | null;
  createdAt: string;
}

function severityClass(severity: AdAlert["severity"]): string {
  if (severity === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }

  return severity === "critical"
    ? "border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 text-[var(--color-destructive)]"
    : "border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 text-[var(--color-warning)]";
}

function formatAlertType(type: string): string {
  return type.replace(/_/g, " ");
}

export default function AdAlertsPage() {
  const [alerts, setAlerts] = useState<AdAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: AdAlert[] }>("/v1/ads/alerts");
      setAlerts(response.data ?? []);
    } catch {
      setAlerts([]);
      setError("Failed to load mounted ad alerts.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Ad Alerts</h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Live alert feed from the shared CRM ad-alert engine. Persisted thresholds, notification delivery, and alert state changes remain intentionally partial.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void fetchAlerts()}
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
        >
          <RefreshCcw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <div className="mb-3 flex items-center gap-2">
          <Bell className="h-4 w-4 text-[var(--color-primary)]" />
          <h2 className="font-medium text-[var(--color-foreground)]">Mounted alert coverage</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {ALERT_TYPES.map((alert) => (
            <div key={alert.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
              <p className="text-sm font-medium text-[var(--color-foreground)]">{alert.label}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{alert.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-medium text-[var(--color-foreground)]">Active alerts</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Read from `GET /v1/ads/alerts`.</p>
          </div>
          <span className="rounded-full bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
            {alerts.length} active
          </span>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-20 animate-pulse rounded-lg bg-[var(--color-surface)]" />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
            <CircleAlert className="h-4 w-4" />
            {error}
          </div>
        ) : alerts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)]">
            No active ad alerts returned by the mounted ads worker.
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div key={alert.id} className={`rounded-lg border p-4 ${severityClass(alert.severity)}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-xs font-medium uppercase tracking-wide">{alert.severity}</span>
                      <span className="rounded-full bg-[var(--color-background)] px-2 py-0.5 text-xs font-medium capitalize text-[var(--color-foreground)]">
                        {formatAlertType(alert.type)}
                      </span>
                    </div>
                    <p className="mt-2 font-medium text-[var(--color-foreground)]">{alert.campaignName ?? "Account alert"}</p>
                    <p className="mt-1 text-sm text-[var(--color-foreground)]">{alert.message}</p>
                  </div>
                  <span className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">{alert.platform ?? "unknown"}</span>
                </div>
                <p className="mt-3 rounded-md bg-[var(--color-background)] p-3 text-sm text-[var(--color-foreground)]">
                  {alert.aiRecommendation}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
        Persisted threshold editing, SMS/email/push delivery, dismiss/snooze state, and full 11-alert runtime depth remain outside this mounted feed tranche.
      </section>
    </div>
  );
}
